import { useCallback, useEffect, useRef, useState } from 'react'
import { CATALOGO_VIDROS, type FerramentaVidro, type IdVidro, type ProtecoesManejo } from '../../dominio/didatico/manejoVidros'
import {
  associarEquipamentosAosVeiculos,
  avaliarEstabilizacaoDaCena,
  executarManejoVidroNoVeiculo,
  type EstadoCortePecaTabletop,
  type TipoObjetoTabletop,
} from '../../dominio/tabletop'
import { alertasDoCorte, alertasDoVidro, descricaoDoCorte } from '../../dominio/tabletop/atendimento'
import type { RetornoUseAtendimentoTabletop } from './useAtendimentoTabletop'
import type { useTabletop } from './useTabletop'

type Tabletop = ReturnType<typeof useTabletop>

const ROTULOS_FERRAMENTA_VIDRO: Readonly<Record<FerramentaVidro, string>> = {
  glasMaster: 'serra corta-vidro',
  puncaoAutomatica: 'punção automática',
}

/** Equipamentos de estabilização oferecidos no cartão do veículo, com o rótulo do catálogo. */
export const EQUIPAMENTOS_ESTABILIZACAO: ReadonlyArray<{ tipo: TipoObjetoTabletop; rotulo: string }> = [
  { tipo: 'calco', rotulo: 'Calço' },
  { tipo: 'cunha', rotulo: 'Cunha' },
  { tipo: 'bloco', rotulo: 'Bloco' },
  { tipo: 'vstrut', rotulo: 'V-Strut' },
  { tipo: 'fitaCatraca', rotulo: 'Fita catraca' },
]

const rotuloEquipamento = (tipo: TipoObjetoTabletop) =>
  EQUIPAMENTOS_ESTABILIZACAO.find((e) => e.tipo === tipo)?.rotulo ?? tipo

export const ROTULOS_PROTECAO: Readonly<Partial<Record<keyof ProtecoesManejo, string>>> = {
  cobertorMaleavel: 'Cobertor maleável sobre a vítima',
  lagrimaProtecao: 'Escudo lágrima',
  mascaraVitima: 'Máscara na vítima',
  mascaraSocorrista: 'Máscara no socorrista',
  oculosSocorrista: 'Óculos no socorrista',
}

/**
 * Cortes, vidros, proteções e estabilização durante o atendimento: executa a
 * ação na cópia da sessão (as mesmas funções do editor, que já agem só no
 * veículo escolhido) e registra a operação com os alertas calculados ANTES
 * da ação — cortar sem estabilizar, por exemplo, é permitido e fica anotado.
 */
export function useOperacoesSessao(tabletop: Tabletop, atendimento: RetornoUseAtendimentoTabletop) {
  const { cena, modo, serialSessao } = tabletop
  const { registrarOperacaoVeiculo } = atendimento
  // veículo cujo cartão pediu o posicionamento de um equipamento
  const [idVeiculoPosicionando, setIdVeiculoPosicionando] = useState<string | null>(null)

  const cortar = useCallback(
    (idVeiculo: string, idPeca: string, estado: EstadoCortePecaTabletop) => {
      const alertas = alertasDoCorte(cena, idVeiculo, idPeca, estado)
      tabletop.executarCorteVeiculo(idVeiculo, idPeca, estado)
      registrarOperacaoVeiculo({ idVeiculo, tipo: 'corte', descricao: descricaoDoCorte(idPeca, estado), alertas })
    },
    [cena, tabletop, registrarOperacaoVeiculo],
  )

  const manejarVidro = useCallback(
    (idVeiculo: string, idVidro: IdVidro, ferramenta: FerramentaVidro) => {
      const previa = executarManejoVidroNoVeiculo(cena, idVeiculo, idVidro, ferramenta)
      if (!previa) return
      tabletop.executarManejoVidro(idVeiculo, idVidro, ferramenta)
      const nome = CATALOGO_VIDROS[idVidro].nome
      registrarOperacaoVeiculo({
        idVeiculo,
        tipo: 'vidro',
        descricao: previa.resultado.sucesso
          ? `${nome}: ${ROTULOS_FERRAMENTA_VIDRO[ferramenta]} (${previa.resultado.novoEstado})`
          : `${nome}: ${ROTULOS_FERRAMENTA_VIDRO[ferramenta]} sem efeito`,
        alertas: alertasDoVidro(previa.resultado),
      })
    },
    [cena, tabletop, registrarOperacaoVeiculo],
  )

  const alternarProtecao = useCallback(
    (idVeiculo: string, chave: keyof ProtecoesManejo) => {
      const protecoes = cena.objetos.find((o) => o.id === idVeiculo)?.veiculo?.protecoes
      if (!protecoes) return
      const ligada = !protecoes[chave]
      tabletop.atualizarVeiculo(idVeiculo, { protecoes: { ...protecoes, [chave]: ligada } })
      registrarOperacaoVeiculo({
        idVeiculo,
        tipo: 'protecao',
        descricao: `${ROTULOS_PROTECAO[chave] ?? chave}: ${ligada ? 'posicionado' : 'retirado'}`,
        alertas: [],
      })
    },
    [cena, tabletop, registrarOperacaoVeiculo],
  )

  /** Leva ao "toque no chão para posicionar" que já existe; o registro sai no toque. */
  const iniciarPosicionamentoEquipamento = useCallback(
    (idVeiculo: string, tipo: TipoObjetoTabletop) => {
      setIdVeiculoPosicionando(idVeiculo)
      tabletop.iniciarPosicionamento(tipo)
    },
    [tabletop],
  )

  /** Substitui `adicionarNaPosicao` em Atender: posiciona na cópia da sessão e registra. */
  const posicionarEquipamento = useCallback(
    (tipo: TipoObjetoTabletop, x: number, z: number) => {
      tabletop.adicionarNaPosicao(tipo, x, z)
      if (!idVeiculoPosicionando) return
      registrarOperacaoVeiculo({
        idVeiculo: idVeiculoPosicionando,
        tipo: 'equipamento_colocado',
        descricao: `${rotuloEquipamento(tipo)} posicionado`,
        alertas: [],
      })
      setIdVeiculoPosicionando(null)
    },
    [tabletop, idVeiculoPosicionando, registrarOperacaoVeiculo],
  )

  const retirarEquipamento = useCallback(
    (idEquipamento: string) => {
      const objeto = cena.objetos.find((o) => o.id === idEquipamento)
      const idVeiculo = associarEquipamentosAosVeiculos(cena.objetos).find((a) => a.idEquipamento === idEquipamento)?.idVeiculo
      tabletop.remover(idEquipamento)
      if (!objeto || !idVeiculo) return
      registrarOperacaoVeiculo({
        idVeiculo,
        tipo: 'equipamento_retirado',
        descricao: `Equipamento retirado: ${objeto.nome}`,
        alertas: [],
      })
    },
    [cena, tabletop, registrarOperacaoVeiculo],
  )

  // "Estabilizado às mm:ss": registra quando a estabilização primária de um
  // veículo passa a concluída durante a sessão. Carros que já começam a
  // sessão estabilizados (preparação do instrutor) não contam.
  const estabilizadosConhecidos = useRef<Set<string> | null>(null)
  const serialVisto = useRef<number | null>(null)
  useEffect(() => {
    if (modo !== 'atender') {
      estabilizadosConhecidos.current = null
      return
    }
    const atuais = new Set(
      avaliarEstabilizacaoDaCena(cena.objetos)
        .filter((a) => a.avaliacao.primariaConcluida)
        .map((a) => a.idVeiculo),
    )
    if (estabilizadosConhecidos.current === null || serialVisto.current !== serialSessao) {
      estabilizadosConhecidos.current = atuais
      serialVisto.current = serialSessao
      return
    }
    for (const id of atuais) {
      if (estabilizadosConhecidos.current.has(id)) continue
      registrarOperacaoVeiculo({
        idVeiculo: id,
        tipo: 'estabilizado',
        descricao: 'Estabilização primária concluída',
        alertas: [],
      })
    }
    estabilizadosConhecidos.current = atuais
  }, [cena.objetos, modo, serialSessao, registrarOperacaoVeiculo])

  return {
    cortar,
    manejarVidro,
    alternarProtecao,
    iniciarPosicionamentoEquipamento,
    posicionarEquipamento,
    retirarEquipamento,
  }
}
